/**
 * STT recovery store — IndexedDB wrapper for unfinished
 * Whisper-takeover recordings.
 *
 * WHY
 *   14-d telemetry on /speech-to-text takeover sessions (2026-05-21): of
 *   164 sessions where whisperTakeover engaged (Web Speech died, MediaRecorder
 *   kept capturing), 29 (~18 %) ended with the user closing the tab BEFORE
 *   stopping the recording → captured audio lost. Whisper never ran.
 *
 *   This store persists each MediaRecorder chunk to IndexedDB while a
 *   takeover is active, so that on the next visit STT can find the
 *   unfinished recording and offer to transcribe it with Whisper.
 *
 *   We only persist when takeover has engaged — normal-flow recordings
 *   (Web Speech works) don't need recovery and write amplification on
 *   every recording would be wasteful.
 *
 * API
 *   await WhisperRecoveryStore.init();                       // open DB
 *   await WhisperRecoveryStore.persist(id, chunks, meta);    // idempotent
 *   await WhisperRecoveryStore.listPending();                // Promise<Array<{id, meta, byteSize}>>
 *   await WhisperRecoveryStore.load(id);                     // Promise<Blob>
 *   await WhisperRecoveryStore.discard(id);                  // remove one
 *   await WhisperRecoveryStore.purgeStale(maxAgeMs);         // remove old entries; returns count
 *
 *   `chunks` is an Array<Blob>; we store a single concatenated Blob keyed
 *   by id. `meta` carries free-form session info (mimeType, lang, model,
 *   takeoverReason, startedAt). `id` should be a UUID generated per
 *   recording session.
 */
(function (global) {
    'use strict';

    var DB_NAME    = 'stt_recovery';
    var DB_VERSION = 1;
    var STORE_NAME = 'sessions';
    var _dbPromise = null;

    function isSupported() {
        return typeof global.indexedDB !== 'undefined';
    }

    function open() {
        if (_dbPromise) return _dbPromise;
        if (!isSupported()) {
            _dbPromise = Promise.reject(new Error('IndexedDB unavailable'));
            return _dbPromise;
        }
        _dbPromise = new Promise(function (resolve, reject) {
            var req = global.indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = function () {
                var db = req.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                }
            };
            req.onsuccess = function () { resolve(req.result); };
            req.onerror = function () { reject(req.error || new Error('IDB open failed')); };
            req.onblocked = function () { reject(new Error('IDB blocked')); };
        });
        return _dbPromise;
    }

    function tx(mode, fn) {
        return open().then(function (db) {
            return new Promise(function (resolve, reject) {
                var t = db.transaction(STORE_NAME, mode);
                var store = t.objectStore(STORE_NAME);
                var result;
                try { result = fn(store); }
                catch (e) { reject(e); return; }
                t.oncomplete = function () { resolve(result && result.value !== undefined ? result.value : result); };
                t.onerror = function () { reject(t.error || new Error('IDB tx failed')); };
                t.onabort = function () { reject(t.error || new Error('IDB tx aborted')); };
            });
        });
    }

    /* Persist (or overwrite) the chunks + meta for a session. Concatenates
       chunks into one Blob keyed by id. Called every time MediaRecorder
       emits a chunk while takeover is active. Idempotent — re-persisting
       with the same id replaces. */
    function persist(id, chunks, meta) {
        if (!id) return Promise.reject(new Error('id required'));
        var mime = (meta && meta.mimeType) || 'audio/webm';
        var blob = new Blob(chunks || [], { type: mime });
        var entry = {
            id: id,
            blob: blob,
            byteSize: blob.size,
            meta: meta || {},
            updatedAt: Date.now(),
        };
        return tx('readwrite', function (store) {
            var r = { value: undefined };
            var req = store.put(entry);
            req.onsuccess = function () { r.value = true; };
            return r;
        });
    }

    /* List metadata for every pending session — does NOT load the Blob
       payload to keep this fast. Caller picks one + calls load(). */
    function listPending() {
        return tx('readonly', function (store) {
            var r = { value: [] };
            var req = store.openCursor();
            req.onsuccess = function (e) {
                var cur = e.target.result;
                if (!cur) return;
                var v = cur.value;
                r.value.push({
                    id:        v.id,
                    byteSize:  v.byteSize || 0,
                    meta:      v.meta || {},
                    updatedAt: v.updatedAt || 0,
                });
                cur.continue();
            };
            return r;
        });
    }

    function load(id) {
        if (!id) return Promise.reject(new Error('id required'));
        return tx('readonly', function (store) {
            var r = { value: null };
            var req = store.get(id);
            req.onsuccess = function () {
                r.value = req.result ? req.result.blob : null;
            };
            return r;
        });
    }

    function discard(id) {
        if (!id) return Promise.reject(new Error('id required'));
        return tx('readwrite', function (store) {
            var r = { value: undefined };
            store.delete(id);
            r.value = true;
            return r;
        });
    }

    /* Auto-cleanup — entries older than maxAgeMs are silently discarded.
       Called on STT page init so a forgotten 1-week-old recording doesn't
       pop up a surprise prompt months later. Returns Promise<deletedCount>. */
    function purgeStale(maxAgeMs) {
        if (!maxAgeMs || maxAgeMs < 0) return Promise.resolve(0);
        var cutoff = Date.now() - maxAgeMs;
        return tx('readwrite', function (store) {
            var r = { value: 0 };
            var req = store.openCursor();
            req.onsuccess = function (e) {
                var cur = e.target.result;
                if (!cur) return;
                if ((cur.value.updatedAt || 0) < cutoff) {
                    cur.delete();
                    r.value++;
                }
                cur.continue();
            };
            return r;
        });
    }

    /* Drop the whole DB — used by tests to start clean. Not part of the
       production API but harmless to expose. */
    function _wipeAll() {
        return tx('readwrite', function (store) {
            store.clear();
            return { value: true };
        });
    }

    global.WhisperRecoveryStore = {
        isSupported: isSupported,
        init:        open,
        persist:     persist,
        listPending: listPending,
        load:        load,
        discard:     discard,
        purgeStale:  purgeStale,
        _wipeAll:    _wipeAll,
    };
})(typeof window !== 'undefined' ? window : this);
